import React, { useState, useEffect } from 'react';
import { subscriptionService } from '../../../services/subscriptionService';
import { Check, Star, Crown, Loader2, AlertCircle } from 'lucide-react';
import Button from '../../../components/ui/Button';

export const PlanComparisonCard = ({ onSelectPlan, selectedTierId }) => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);

  useEffect(() => {
    loadPlans();
  }, []);

  const loadPlans = async () => {
    try {
      setLoading(true);
      const activePlans = await subscriptionService?.getActiveTiers();
      setPlans(activePlans || []);
    } catch (err) {
      console.error('Failed to load plans:', err);
      setError('Unable to load subscription plans. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const getPlanBadge = (planName) => {
    const name = planName?.toLowerCase();
    if (name?.includes('starter') || name?.includes('basic')) {
      return { label: 'Best for Starters', color: 'bg-blue-100 text-blue-700', icon: Star };
    }
    if (name?.includes('professional') || name?.includes('pro')) {
      return { label: 'Most Popular', color: 'bg-purple-100 text-purple-700', icon: Crown };
    }
    if (name?.includes('enterprise') || name?.includes('premium')) {
      return { label: 'Best Value', color: 'bg-green-100 text-green-700', icon: Crown };
    }
    return null;
  };

  const formatPrice = (plan) => {
    const basePrice = Number(plan?.base_price || 0);
    const perUserPrice = Number(plan?.per_user_price || 0);
    const total = basePrice + perUserPrice;

    if (plan?.price_type === 'per_user') {
      return {
        price: `$${total}`,
        period: 'per user/month',
        description: perUserPrice > 0 ? `$${basePrice} base + $${perUserPrice}/user` : null
      };
    }
    return {
      price: `$${total}`,
      period: 'per month',
      description: null
    };
  };

  const formatLimits = (limits) => {
    if (!limits || typeof limits !== 'object') return [];
    
    const limitLabels = {
      max_users: 'Users',
      max_deals: 'Deals',
      max_contacts: 'Contacts',
      max_storage_gb: 'Storage (GB)',
      max_email_sends: 'Email Sends',
      max_api_calls: 'API Calls'
    };

    return Object?.entries(limits)?.map(([key, value]) => ({
      label: limitLabels?.[key] || key?.replace(/_/g, ' ')?.replace(/\b\w/g, l => l?.toUpperCase()),
      value: value === -1 ? 'Unlimited' : value
    }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 text-blue-600 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-red-600 mt-0.5 flex-shrink-0" />
        <div className="flex-1">
          <p className="text-sm text-red-800">{error}</p>
          <button
            onClick={loadPlans}
            className="text-xs text-red-600 hover:text-red-700 underline mt-1"
          >
            Retry loading plans
          </button>
        </div>
      </div>
    );
  }

  if (!plans?.length) {
    return null;
  }

  return (
    <div className="mb-8">
      {/* Comparison Header */}
      <div className="text-center mb-6">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Choose Your Plan
        </h2>
        <p className="text-gray-600">
          Select the perfect plan for your business needs
        </p>
      </div>
      {/* Plans Grid */}
      <div className={`grid gap-6 ${plans?.length === 2 ? 'md:grid-cols-2' : plans?.length === 3 ? 'md:grid-cols-3' : 'md:grid-cols-2 lg:grid-cols-3'}`}>
        {plans?.map((plan) => {
          const badge = getPlanBadge(plan?.name);
          const pricing = formatPrice(plan);
          const limits = formatLimits(plan?.limits);
          const isSelected = plan?.id === selectedTierId;

          return (
            <div
              key={plan?.id}
              className={`relative bg-white rounded-xl border-2 transition-all duration-200 ${
                isSelected
                  ? 'border-blue-600 shadow-lg scale-[1.02]'
                  : 'border-gray-200 hover:border-blue-300 hover:shadow-md'
              }`}
            >
              {/* Badge */}
              {badge && (
                <div className={`absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 rounded-full text-xs font-semibold flex items-center gap-1 ${badge?.color}`}>
                  <badge.icon className="w-3 h-3" />
                  {badge?.label}
                </div>
              )}
              <div className="p-6">
                {/* Plan Name */}
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {plan?.name}
                </h3>

                {/* Pricing */}
                <div className="mb-4">
                  <div className="flex items-baseline gap-1">
                    <span className="text-4xl font-bold text-gray-900">
                      {pricing?.price}
                    </span>
                    <span className="text-gray-600">
                      /{pricing?.period}
                    </span>
                  </div>
                  {pricing?.description && (
                    <p className="text-xs text-gray-500 mt-1">
                      {pricing?.description}
                    </p>
                  )}
                </div>

                {/* Features (collapsed by default, expandable) */}
                <div className="mb-4">
                  <div className="space-y-2">
                    {plan?.features?.slice(0, isExpanded ? undefined : 4)?.map((feature, idx) => (
                      <div key={idx} className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                        <span className="text-sm text-gray-700">{feature}</span>
                      </div>
                    ))}
                  </div>

                  {plan?.features?.length > 4 && (
                    <button
                      onClick={() => setIsExpanded(!isExpanded)}
                      className="text-xs text-blue-600 hover:text-blue-700 mt-2 font-medium"
                    >
                      {isExpanded ? 'Show less' : `Show ${plan?.features?.length - 4} more features`}
                    </button>
                  )}
                </div>

                {/* Limits */}
                {limits?.length > 0 && (
                  <div className="border-t pt-4 mb-4">
                    <h4 className="text-xs font-semibold text-gray-700 uppercase mb-2">
                      Limits
                    </h4>
                    <div className="grid grid-cols-2 gap-2">
                      {limits?.map((limit, idx) => (
                        <div key={idx} className="text-xs">
                          <span className="text-gray-600">{limit?.label}:</span>
                          <span className="font-semibold text-gray-900 ml-1">
                            {limit?.value}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Select Button */}
                <Button
                  onClick={() => onSelectPlan?.(plan?.id)}
                  className={`w-full ${
                    isSelected
                      ? 'bg-blue-600 text-white hover:bg-blue-700' :'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {isSelected ? 'Selected' : 'Select Plan'}
                </Button>
              </div>
            </div>
          );
        })}
      </div>
      {/* Additional Info */}
      <div className="text-center mt-6">
        <p className="text-sm text-gray-600">
          All plans include 14-day free trial • No credit card required • Cancel anytime
        </p>
      </div>
    </div>
  );
};

export default PlanComparisonCard;